import * as React from "react";
import { Field } from "./Field";

type Props = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  requirement?: "required" | "optional";
  hint?: string;
  error?: string;
  disabled?: boolean;
  /** Leading badge → kit input-group (currency / dial code). */
  addon?: string;
  id?: string;
  name?: string;
  autoComplete?: string;
  /** Render the label visually hidden (still accessible) — for compact inline
   *  forms that rely on the placeholder, e.g. the footer newsletter row. */
  hideLabel?: boolean;
};

// 15px matches kit .input (distinct from 14.5px text-text token). Mobile bumps
// to 16px because iOS Safari auto-zooms any focused input below 16px.
const INPUT_BASE =
  "w-full bg-white font-sans font-normal tracking-normal text-primary text-[16px] md:text-[15px] leading-snug placeholder:text-primary/30 outline-none transition-[border-color,background] duration-200";

// iOS Safari renders input[type=date] as a native control whose intrinsic
// width beats `width: 100%`, so the field overflows its container (visible on
// the booking sheet, where it pushed past the right edge). Dropping the native
// appearance turns it back into a normal box; the value also stops being
// centred, so it lines up with every other field.
//
// Without the native control the box height comes from the inner value
// element, which is empty — and therefore zero-height — until a date is
// picked, so an untouched field rendered shorter than its neighbours. Give
// that element the same line box as typed text (`leading-snug` = 1.375) so
// empty and filled date fields both match a plain text input.
const NATIVE_DATE_RESET =
  "appearance-none min-w-0 [&::-webkit-date-and-time-value]:text-left " +
  "[&::-webkit-date-and-time-value]:min-h-[1.375em]";

export function TextField({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  requirement,
  hint,
  error,
  disabled,
  addon,
  id,
  name,
  autoComplete,
  hideLabel,
}: Props) {
  const reactId = React.useId();
  const inputId = id ?? reactId;
  const base =
    type === "date" ? `${INPUT_BASE} ${NATIVE_DATE_RESET}` : INPUT_BASE;

  const input = (
    <input
      id={inputId}
      name={name}
      type={type}
      value={value}
      autoComplete={autoComplete}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      aria-invalid={error && !disabled ? true : undefined}
    />
  );

  if (addon) {
    return (
      <Field
        label={label}
        requirement={requirement}
        hint={hint}
        error={error}
        htmlFor={inputId}
        hideLabel={hideLabel}
      >
        <div
          className={`flex items-stretch border bg-white transition-colors duration-200 focus-within:border-primary ${
            error ? "border-error" : "border-primary/15"
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <span className="inline-flex flex-none items-center justify-center border-r border-primary/15 bg-primary/10 px-4 text-micro font-semibold uppercase tracking-button text-primary">
            {addon}
          </span>
          {React.cloneElement(input, {
            className: `${base} flex-1 border-0 px-4 py-3.5`,
          })}
        </div>
      </Field>
    );
  }

  const stateCls = disabled
    ? "bg-[color:var(--neutral-100)] text-primary/50 cursor-not-allowed border-primary/15"
    : error
      ? "border-error pl-[19px] shadow-[inset_3px_0_0_var(--error)]"
      : "border-primary/15 hover:border-primary/50 focus:border-primary";

  return (
    <Field
      label={label}
      requirement={requirement}
      hint={hint}
      error={error}
      htmlFor={inputId}
      hideLabel={hideLabel}
    >
      {React.cloneElement(input, {
        className: `${base} border px-4 py-3.5 ${stateCls}`,
      })}
    </Field>
  );
}
